import React, {useEffect, useMemo} from "react";
import * as Yup from "yup";
import ValidateMessages from "../../../../core/models/static/texts/vallidate-messages";
import {makeRequired, makeValidate} from "mui-rff";
import Form from "../../base/form";
import {Col, Row} from "reactstrap";
import {InputForm} from "../../base/input/input";
import {Dropdown, DropdownForm} from "../../base/dropdown/dropdown";
import Button from "../../base/button/button";
import useIndustry from "../../../hooks/use-industry";
import {selectedCategoryHook} from "../../../hooks/use-selected-category";

const formNames = {
    industry: 'industry',
    keyword: 'keyword',
    category: 'category',
}

const schema = Yup.object().shape({
    [formNames.keyword]: Yup.string().trim().nullable().min(3, ValidateMessages.minLength('Keywords', 3)),
    [formNames.industry]: Yup.object().nullable(),
    [formNames.category]: Yup.object().nullable(),
});

const validate = makeValidate(schema);
const required = makeRequired(schema);

const FastConnectionSearchSection = ({searchData}) => {
    const {
        industries,
        categoriesOfFetchedIndustry: categories,
        fetchedIndustry,
        fetchIndustry,
        cachedIndustry,
        hasNoIndustries,
    } = useIndustry();
    /**
     * With initial render of the components:
     * - if the industry is enabled, fetch the industry from the server.
     */
    useEffect(() => {
        if (searchData.filters?.industry?.id) {
            fetchIndustry({industryId: searchData.filters?.industry?.id}).then()
        } else if (cachedIndustry?.id) {
            fetchIndustry({industryId: cachedIndustry?.id}).then();
        }
    }, [fetchIndustry, cachedIndustry?.id]);

    /**
     * Initial values of the form based on the query of the url
     * @type {object}
     */
    const initialValues = useMemo(() => {
        return {
            [formNames.keyword]: searchData.filters?.keyword ?? null,
            [formNames.industry]: searchData.filters?.industry ?? fetchedIndustry,
            [formNames.category]: searchData.filters?.category,
        }
    }, [searchData.filters])

    /**
     * Changes the query params of the
     * @param values {any} values form the form
     */
    const onSubmit = (values) => {
        searchData.setFilters({
            keyword: values[formNames.keyword]?.trim() ? values[formNames.keyword]?.trim() : undefined,
            category: values[formNames.category],
        })
    };

    /**
     * Empties the sub-category values of a particular category that has been changed.
     * @param e
     * @param form
     */
    const onIndustryChange = (e, form) => {
        form.change('category', null);
        form.change('industry', e.target.value);
        fetchIndustry({industryId: e.target.value?.id}).then();
    }


    return (
        <Form
            onSubmit={onSubmit}
            validate={validate}
            className={'mt-4'}
            initialValues={initialValues}
            render={({submitting, values, form}) => {
                const {getSubCategories, getSelectedCategoriesHierarchy} = selectedCategoryHook(categories, values.category);
                const selectedCategoriesHierarchy = getSelectedCategoriesHierarchy();
                const subCategories = getSubCategories();
                return (
                    <Row className='no-gutters justify-content-start pt-2'>
                        <Col xs={12} className='px-2 mb-4'>
                            <InputForm
                                onChange={(val) => form.change(formNames.keyword, val)}
                                value={values?.keyword ?? ''}
                                label={'Keyword'}
                                name={formNames.keyword}
                                type={'text'}
                                required={required[formNames.keyword]}
                                placeholder={'part number, OE number, & AM number...'}
                            />
                        </Col>
                        {
                            !hasNoIndustries &&
                            <>
                                {
                                    industries.length > 1 &&
                                    <Col xs={12} md={6} className='px-2 mb-4'>
                                        <DropdownForm
                                            label={'Industry'}
                                            name={formNames.industry}
                                            required={false}
                                            data={industries?.map(e => ({value: e, label: e.title, id: e.id})) ?? []}
                                            onChange={(e) => onIndustryChange(e, form)}
                                        />
                                    </Col>
                                }
                                {
                                    selectedCategoriesHierarchy.map((category, index) => (
                                        <Col xs={12} md={6} className='px-2 mb-4'>
                                            <Dropdown
                                                label={index === 0 ? `Category` : "Sub category"}
                                                data={category.options?.map(e => ({label: e.title, value: e.id, id: e.id})) ?? []}
                                                onChange={(e) => form.change('category', e.target.value)}
                                                value={category.id}
                                                renderSelectedValue={() => category.title}
                                            />
                                        </Col>
                                    ))
                                }
                                {
                                    subCategories.length > 0 &&
                                    <Col xs={12} md={6} className='px-2 mb-4'>
                                        <DropdownForm
                                            label={selectedCategoriesHierarchy.length ? "Sub category" : "Category"}
                                            name={`category`}
                                            required={required.category}
                                            data={subCategories?.map(e => ({label: e.title, value: e.id, id: e.id,})) ?? []}
                                            onChange={(e) => form.change('category', e.target.value)}
                                            value={selectedCategoriesHierarchy[selectedCategoriesHierarchy.length - 1]?.id ?? ""}
                                            renderSelectedValue={(e) => subCategories.find(c => c.id === e)?.title}
                                        />
                                    </Col>
                                }
                            </>
                        }
                        <Col xs={12} className='px-3 mt-2 align-items-center row no-gutters justify-content-center align-items-center'>
                            <Button
                                disabled={!!submitting || searchData.loading}
                                type={'submit'}
                                variant={'contained'}
                                color={'primary'}
                                className='py-3 px-8'
                            >
                                Search
                            </Button>
                        </Col>
                    </Row>
                );
            }}
        />
    );
}

export default FastConnectionSearchSection;
